import { Users } from 'react-feather';
import { useFeatureGuard } from '~/common/guards/use-feature-guard';
import { useMyRole } from '~/common/providers/auth/use-login-user';
import {
  NavigationGroup,
  NavigationLink,
  NavigationMenuItem,
} from '~/common/components/layout/navigation/NavigationBody';
import { useFetchWorkforcePlanningAllowedAction } from '~/features/workforce-planning/common/use-fetch-workforce-planning-allowed-action';
import type { Role } from '~/common/providers/auth/role';
import { FetchWorkforcePlanningAllowedActionReturnAllowedActionsEnum } from '~/common/api';

const useWorkforcePlanningNavigationItems = (
  isCurrentPage: (targetUrl: string) => boolean,
): Return => {
  const isWorkforcePlanningEnabled = useFeatureGuard(['isWorkforcePlanningEnabled']);
  const isNewWorkforcePlanningSimulationEnabled = useFeatureGuard([
    'isNewWorkforcePlanningSimulationEnabled',
  ]);
  const isOldWorkforcePlanningSimulationEnabled = useFeatureGuard([
    'isOldWorkforcePlanningSimulationEnabled',
  ]);
  const { data: res } = useFetchWorkforcePlanningAllowedAction();
  const myRole = useMyRole();

  if (!isWorkforcePlanningEnabled || !res || !myRole)
    return { workforcePlanningSection: undefined };

  const children = buildWorkforcePlanningNavigationItems({
    isCurrentPage,
    allowedActions: res.allowedActions,
    myRole,
    isNewWorkforcePlanningSimulationEnabled,
    isOldWorkforcePlanningSimulationEnabled,
  });

  if (children.length === 0) return { workforcePlanningSection: undefined };

  return {
    workforcePlanningSection: {
      key: 'workforce-planning',
      text: '人員計画',
      icon: <Users />,
      children,
    },
  };
};

type BuildWorkforcePlanningNavigationItemsParams = {
  isCurrentPage: (targetUrl: string) => boolean;
  allowedActions: FetchWorkforcePlanningAllowedActionReturnAllowedActionsEnum[];
  myRole: Role;
  isNewWorkforcePlanningSimulationEnabled: boolean;
  isOldWorkforcePlanningSimulationEnabled: boolean;
};

const buildWorkforcePlanningNavigationItems = ({
  isCurrentPage,
  allowedActions,
  myRole,
  isNewWorkforcePlanningSimulationEnabled,
  isOldWorkforcePlanningSimulationEnabled,
}: BuildWorkforcePlanningNavigationItemsParams): (NavigationGroup | NavigationLink)[] => {
  const isAdmin = myRole === 'LOGLASS_SYSTEM_ADMIN' || myRole === 'TENANT_ADMIN';
  const isPlanner = myRole === 'CORP_PLANNING' || myRole === 'MANAGER';
  // 閲覧者（GENERAL）とIT担当（IT_STAFF）には人員計画メニューを表示しない
  if (!isAdmin && !isPlanner) return [];

  const canCrudWorkforceActual = allowedActions.includes(
    FetchWorkforcePlanningAllowedActionReturnAllowedActionsEnum.CrudWorkforceActual,
  );
  const canCrudUnitAmount = allowedActions.includes(
    FetchWorkforcePlanningAllowedActionReturnAllowedActionsEnum.CrudUnitAmount,
  );

  // 予算・見込
  const budgetsForecastsGroup: NavigationGroup = {
    key: 'workforce-planning-budgets-forecasts',
    id: 'workforce-planning-budgets-forecasts',
    text: '予算・見込',
    type: 'group',
    children: [
      ...buildSimulationItems({
        isCurrentPage,
        isNewWorkforcePlanningSimulationEnabled,
        isOldWorkforcePlanningSimulationEnabled,
      }),
      {
        key: 'workforce-planning-workforce-slots',
        text: '人員計画枠',
        targetUrl: '/workforce-planning/workforce-slots',
        isActive: isCurrentPage('/workforce-planning/workforce-slots'),
      },
    ],
  };

  // 人員情報
  const personInfoGroup: NavigationGroup = {
    key: 'workforce-planning-person-info',
    id: 'workforce-planning-person-info',
    text: '人員情報',
    type: 'group',
    children: [
      canCrudWorkforceActual && {
        key: 'workforce-planning-person-list',
        text: '人員',
        targetUrl: '/workforce-planning/person-list',
        isActive: isCurrentPage('/workforce-planning/person-list'),
      },
      isAdmin && {
        key: 'workforce-planning-workforce-actual-import-mapping-settings',
        text: '人員の取込設定',
        targetUrl: '/workforce-planning/workforce-actual-import-mapping-settings',
        isActive: isCurrentPage('/workforce-planning/workforce-actual-import-mapping-settings'),
      },
      canCrudUnitAmount && {
        key: 'workforce-planning-unit-amounts',
        text: '人件費単価',
        targetUrl: '/workforce-planning/unit-amounts',
        isActive: isCurrentPage('/workforce-planning/unit-amounts'),
      },
      isAdmin && {
        key: 'upload-workforce/actuals',
        text: '工数実績',
        targetUrl: '/upload/workforce/actuals',
        isActive:
          isCurrentPage('/upload/workforce/actuals') || isCurrentPage('/upload/workforce/apply'),
      },
    ].filter(truthy),
  };

  // 人件費計算モデル（管理者のみ）
  const calculationModelGroup: NavigationGroup = {
    key: 'workforce-planning-calculation-model',
    id: 'workforce-planning-calculation-model',
    text: '人件費計算モデル',
    type: 'group',
    children: [
      {
        key: 'workforce-planning-to-financial-item-conversion-rulesets',
        text: '科目変換ルール',
        targetUrl: '/workforce-planning/to-financial-item/conversion-rulesets',
        // ルールセット配下（conversion-rulesets/...）に加え、ルール編集ページ（conversion-rules/...）でも活性にする
        isActive:
          isCurrentPage('/workforce-planning/to-financial-item/conversion-rulesets') ||
          isCurrentPage('/workforce-planning/to-financial-item/conversion-rules'),
      },
      {
        key: 'workforce-planning-to-financial-item/base-salary-increase',
        text: '昇給',
        targetUrl: '/workforce-planning/to-financial-item/base-salary-increase',
        isActive: isCurrentPage('/workforce-planning/to-financial-item/base-salary-increase'),
      },
      {
        key: 'workforce-planning-to-financial-item-unit-amount-tables',
        text: '枠の標準単価',
        targetUrl: '/workforce-planning/to-financial-item/unit-amount-tables',
        isActive: isCurrentPage('/workforce-planning/to-financial-item/unit-amount-tables'),
      },
    ],
  };

  // 人員計画権限管理（管理者のみ）
  const permissionGroupLink: NavigationLink = {
    key: 'workforce-planning-permission-groups',
    text: '人員計画権限管理',
    targetUrl: '/workforce-planning/permission-groups',
    isActive: isCurrentPage('/workforce-planning/permission-groups'),
  };

  return [
    budgetsForecastsGroup,
    personInfoGroup,
    isAdmin && calculationModelGroup,
    isAdmin && permissionGroupLink,
  ]
    .filter(truthy)
    .filter((item) => item.type !== 'group' || item.children.length > 0);
};

type BuildSimulationItemsParams = {
  isCurrentPage: (targetUrl: string) => boolean;
  isNewWorkforcePlanningSimulationEnabled: boolean;
  isOldWorkforcePlanningSimulationEnabled: boolean;
};

/**
 * 人員計画シミュレーションのナビゲーション項目を新旧フィーチャートグルに応じて構築する。
 * - 新のみ有効: 「人員計画シミュレーション」を新機能へ遷移させる1項目
 * - 旧のみ有効: 「人員計画シミュレーション」を旧機能へ遷移させる1項目
 * - 新旧両方有効: 「人員計画シミュレーション(新)」「人員計画シミュレーション(旧)」の2項目
 * - 両方無効: 項目なし
 */
const buildSimulationItems = ({
  isCurrentPage,
  isNewWorkforcePlanningSimulationEnabled,
  isOldWorkforcePlanningSimulationEnabled,
}: BuildSimulationItemsParams): NavigationLink[] => {
  const bothEnabled =
    isNewWorkforcePlanningSimulationEnabled && isOldWorkforcePlanningSimulationEnabled;
  const items: NavigationLink[] = [];

  if (isNewWorkforcePlanningSimulationEnabled) {
    items.push({
      key: 'in-development-workforce-planning-simulations',
      text: bothEnabled ? '人員計画シミュレーション(新)' : '人員計画シミュレーション',
      targetUrl: '/workforce-planning/in-development/simulations',
      isActive: isCurrentPage('/workforce-planning/in-development/simulations'),
    });
  }
  if (isOldWorkforcePlanningSimulationEnabled) {
    items.push({
      key: 'workforce-planning-simulations',
      text: bothEnabled ? '人員計画シミュレーション(旧)' : '人員計画シミュレーション',
      targetUrl: '/workforce-planning/simulations',
      isActive: isCurrentPage('/workforce-planning/simulations'),
    });
  }

  return items;
};

export { useWorkforcePlanningNavigationItems, buildWorkforcePlanningNavigationItems };

type Return = {
  workforcePlanningSection?: NavigationMenuItem;
};

type Truthy<T> = T extends false | '' | 0 | null | undefined ? never : T;

function truthy<T>(value: T): value is Truthy<T> {
  return !!value;
}
